import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, ChevronDown, Loader2 } from "lucide-react";
import { SOLUTION_OPTIONS, INDUSTRY_OPTIONS, FLEET_SIZES } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", email: "", phone: "", industry: "", fleet_size: "", solution: "", message: "" };
const inputCls = "h-12 rounded-xl border-line bg-navy-800/60 text-white placeholder:text-slate-500 focus-visible:ring-cyan-400";
const selectCls = "h-12 w-full appearance-none rounded-xl border border-line bg-navy-800/60 px-3 pr-10 text-sm outline-none focus-visible:ring-1 focus-visible:ring-cyan-400";

const Field = ({ id, label, children, required }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}{required && <span className="text-cyan-400"> *</span>}</Label>
    {children}
  </div>
);

const SelectField = ({ id, value, onChange, options, placeholder }) => (
  <div className="relative">
    <select
      id={id}
      data-testid={`contact-${id}-select`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${selectCls} ${value ? "text-white" : "text-slate-500"}`}
    >
      <option value="" disabled className="text-slate-500">{placeholder}</option>
      {options.map((o) => <option key={o} value={o} className="bg-navy-800 text-white">{o}</option>)}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
  </div>
);

export const ContactForm = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const solutionParam = params.get("solution");
  const industryParam = params.get("industry");

  useEffect(() => {
    setForm((f) => {
      const solution = SOLUTION_OPTIONS.includes(solutionParam) ? solutionParam : f.solution;
      const industry = INDUSTRY_OPTIONS.includes(industryParam) ? industryParam : f.industry;
      return solution === f.solution && industry === f.industry ? f : { ...f, solution, industry };
    });
  }, [solutionParam, industryParam]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.solution) return toast.error("Please select the solution you require.");
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      const { data } = await axios.post(`${API}/leads`, payload);
      setDone(data);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="card p-10 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-cyan-400 text-navy"><Check className="h-6 w-6" /></span>
        <h3 className="mt-6 text-2xl font-semibold">Enquiry received</h3>
        <p className="mt-3 text-slate-400">Thank you, {done.name}. A Wexoz specialist will review your <span className="text-white">{done.solution}</span> requirement and get back to you.</p>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-wider text-slate-500">Reference {done.id.slice(0, 8)}</p>
        <button data-testid="contact-new-enquiry" onClick={() => { setDone(null); setForm(empty); }} className="btn-ghost mt-8">Submit another enquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="card p-6 sm:p-8 lg:p-10">
      {form.solution && (
        <div data-testid="contact-preselected-banner" className="mb-8 flex items-center gap-3 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-sm text-cyan-100">
          <span className="h-2 w-2 rounded-full bg-cyan-400" /> Enquiry for <strong className="font-semibold text-white">{form.solution}</strong> — you can change this below.
        </div>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        <Field id="name" label="Name" required><Input id="name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} /></Field>
        <Field id="company" label="Company" required><Input id="company" data-testid="contact-company-input" required minLength={2} value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} /></Field>
        <Field id="email" label="Work Email" required><Input id="email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} placeholder="name@company.com" className={inputCls} /></Field>
        <Field id="phone" label="Phone"><Input id="phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+971 ..." className={inputCls} /></Field>
        <Field id="industry" label="Industry"><SelectField id="industry" value={form.industry} onChange={set("industry")} options={INDUSTRY_OPTIONS} placeholder="Select industry" /></Field>
        <Field id="fleet_size" label="Fleet Size"><SelectField id="fleet_size" value={form.fleet_size} onChange={set("fleet_size")} options={FLEET_SIZES} placeholder="Select fleet size" /></Field>
        <div className="sm:col-span-2">
          <Field id="solution" label="Solution Required" required><SelectField id="solution" value={form.solution} onChange={set("solution")} options={SOLUTION_OPTIONS} placeholder="Select the solution you need" /></Field>
        </div>
        <div className="sm:col-span-2">
          <Field id="message" label="Message"><Textarea id="message" data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your operation, fleet and the challenge you want to solve." className="rounded-xl border-line bg-navy-800/60 text-white placeholder:text-slate-500 focus-visible:ring-cyan-400" /></Field>
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">We use your details only to respond to this enquiry.</p>
        <button type="submit" disabled={busy} data-testid="contact-form-submit-button" className="btn-primary disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />} {busy ? "Sending..." : "Send enquiry"}
        </button>
      </div>
    </form>
  );
};
